import * as React from 'react';
import dayjs from 'dayjs';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Alert from '@mui/joy/Alert';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import Chip from '@mui/joy/Chip';
import CircularProgress from '@mui/joy/CircularProgress';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import IconButton from '@mui/joy/IconButton';
import Input from '@mui/joy/Input';
import Modal from '@mui/joy/Modal';
import ModalClose from '@mui/joy/ModalClose';
import ModalDialog from '@mui/joy/ModalDialog';
import Option from '@mui/joy/Option';
import Radio from '@mui/joy/Radio';
import RadioGroup from '@mui/joy/RadioGroup';
import Select from '@mui/joy/Select';
import Sheet from '@mui/joy/Sheet';
import Stack from '@mui/joy/Stack';
import Table from '@mui/joy/Table';
import Textarea from '@mui/joy/Textarea';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import { EditRounded, FavoriteRounded, FileDownloadRounded, ReceiptLong, RefreshRounded, SearchRounded, VisibilityRounded } from '@mui/icons-material';

import ExportModal, { ExportColumn } from '../components/nitya-seva/ExportModal';
import Header from '../components/common/Header';
import Sidebar from '../components/common/Sidebar';
import PageBreadcrumbs from '../components/common/PageBreadcrumbs';
import {
  fetchDonationForEdit,
  fetchNityaSevaMonthDetail,
  NityaSevaMonthDetailItem,
  NityaSevaMonthDetailResponse,
  NityaSevaPaymentFrequency,
  recordNityaSevaManualDonation,
  updateDonationById,
} from '../api';

const modeLabel: Record<string, string> = {
  ecs_nach: 'ECS / NACH',
  auto_razorpay: 'Auto Razorpay',
  online_transfer: 'Online Transfer',
  cash_first_sunday: 'Cash 1st Sunday',
};

const statusColor = (status?: string) => {
  const value = (status || '').toLowerCase();
  if (value === 'due' || value === 'unpaid' || value.includes('not paid')) return 'danger';
  if (value === 'success' || value === 'paid' || value === 'captured' || value === 'verified') return 'success';
  if (value === 'pending') return 'warning';
  if (value === 'failed') return 'danger';
  return 'neutral';
};

const selectedMonthPaymentLabel = (item: NityaSevaMonthDetailItem) => (
  item.history.kind === 'due' ? 'Not paid yet' : 'Paid'
);

type SortDirection = 'asc' | 'desc';
type DetailSortKey = 'fullName' | 'whatsAppNumber' | 'phone' | 'email' | 'submissionMode' | 'pledgeAmount' | 'pledgeTier' | 'status' | 'paymentMethod' | 'source' | 'donationId' | 'recordedOn' | 'transactionId' | 'note';

const compareText = (a?: string | number | null, b?: string | number | null) => (
  String(a ?? '').localeCompare(String(b ?? ''), undefined, { numeric: true, sensitivity: 'base' })
);

const compareNumbers = (a?: number | string | null, b?: number | string | null) => (
  Number(a ?? 0) - Number(b ?? 0)
);

const compareDates = (a?: string | null, b?: string | null) => {
  const first = a ? dayjs(a).valueOf() : 0;
  const second = b ? dayjs(b).valueOf() : 0;
  return first - second;
};

const applySortDirection = (value: number, direction: SortDirection) => (
  direction === 'asc' ? value : -value
);

const renderSortableHeader = (
  label: string,
  active: boolean,
  direction: SortDirection,
  onClick: () => void,
) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      all: 'unset',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.4,
      color: active ? 'primary.600' : 'inherit',
      font: 'inherit',
      fontWeight: 700,
      '&:hover': { color: 'primary.500' },
    }}
  >
    <span>{label}</span>
    <Box component="span" aria-hidden="true" sx={{ fontSize: '0.8rem' }}>
      {active ? (direction === 'asc' ? '↑' : '↓') : '↕'}
    </Box>
  </Box>
);

const paymentFrequencyOptions: Array<{ value: NityaSevaPaymentFrequency; label: string; months: number }> = [
  { value: 'monthly', label: 'Monthly', months: 1 },
  { value: 'quarterly', label: 'Quarterly', months: 3 },
  { value: 'half_yearly', label: 'Half-Yearly', months: 6 },
  { value: 'annually', label: 'Annually', months: 12 },
];

const getPaymentFrequencyMonths = (value: NityaSevaPaymentFrequency) => paymentFrequencyOptions.find((option) => option.value === value)?.months || 1;
const getPaymentFrequencyLabel = (value: NityaSevaPaymentFrequency) => paymentFrequencyOptions.find((option) => option.value === value)?.label || 'Monthly';
const getCycleAmount = (monthlyAmount: number, value: NityaSevaPaymentFrequency) => Number(monthlyAmount || 0) * getPaymentFrequencyMonths(value);
const getPaymentFrequencyFromMonths = (months: number): NityaSevaPaymentFrequency => {
  if (months >= 12) return 'annually';
  if (months >= 6) return 'half_yearly';
  if (months >= 3) return 'quarterly';
  return 'monthly';
};
const parseNityaSevaCoverageMeta = (value?: string) => {
  const match = String(value || '').match(/Nitya Seva Coverage:\s*(\d{4}-\d{2})\|(\d+)/i);
  if (!match) {
    return null;
  }
  return {
    forMonth: match[1],
    frequency: getPaymentFrequencyFromMonths(Number(match[2] || 1)),
  };
};
const buildNityaSevaCoverageMeta = (forMonth: string, frequency: NityaSevaPaymentFrequency) => `Nitya Seva Coverage: ${forMonth}|${getPaymentFrequencyMonths(frequency)}`;

export default function NityaSevaMonthDetails() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const month = searchParams.get('month');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [status, setStatus] = React.useState('');
  const [mode, setMode] = React.useState('');
  const [q, setQ] = React.useState('');
  const [enrollmentSourceFilter, setEnrollmentSourceFilter] = React.useState('');
  const [paymentFilter, setPaymentFilter] = React.useState('');
  const [detailSort, setDetailSort] = React.useState<{ key: DetailSortKey; direction: SortDirection }>({ key: 'fullName', direction: 'asc' });
  const [detailData, setDetailData] = React.useState<NityaSevaMonthDetailResponse | null>(null);
  const [manualTargetRow, setManualTargetRow] = React.useState<NityaSevaMonthDetailItem | null>(null);
  const [manualMode, setManualMode] = React.useState<'create' | 'edit'>('create');
  const [editingDonationSnapshot, setEditingDonationSnapshot] = React.useState<Record<string, any> | null>(null);
  const [savingManualDonation, setSavingManualDonation] = React.useState(false);
  const [manualAmount, setManualAmount] = React.useState('1100');
  const [manualDate, setManualDate] = React.useState(dayjs().format('YYYY-MM-DD'));
  const [manualForMonth, setManualForMonth] = React.useState(month || dayjs().format('YYYY-MM'));
  const [manualPaymentFrequency, setManualPaymentFrequency] = React.useState<NityaSevaPaymentFrequency>('monthly');
  const [manualTransactionId, setManualTransactionId] = React.useState('');
  const [manualMethod, setManualMethod] = React.useState('Cash');
  const [manualNote, setManualNote] = React.useState('');
  const [exportOpen, setExportOpen] = React.useState(false);

  const normalizeDueNote = (note?: string) => {
    const trimmed = (note || '').trim();
    if (!trimmed) {
      return '—';
    }
    const lower = trimmed.toLowerCase();
    if (lower.includes('no settled nitya seva donation')) {
      return '—';
    }
    return trimmed;
  };

  const handleDetailSort = React.useCallback((key: DetailSortKey) => {
    setDetailSort((prev) => (
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    ));
  }, []);

  const filteredSortedItems = React.useMemo(() => {
    const next = Array.isArray(detailData?.items) ? [...detailData.items] : [];

    const filtered = next.filter((item) => {
      if (enrollmentSourceFilter && (item.enrollment.enrollmentSource || 'online') !== enrollmentSourceFilter) {
        return false;
      }
      if (paymentFilter) {
        const state = item.history.kind === 'due' ? 'unpaid' : 'paid';
        return state === paymentFilter;
      }
      return true;
    });

    filtered.sort((a, b) => {
      let result = 0;
      switch (detailSort.key) {
        case 'fullName':
          result = compareText(a.enrollment.fullName, b.enrollment.fullName);
          break;
        case 'whatsAppNumber':
          result = compareText(a.enrollment.whatsAppNumber, b.enrollment.whatsAppNumber);
          break;
        case 'phone':
          result = compareText(a.enrollment.phone, b.enrollment.phone);
          break;
        case 'email':
          result = compareText(a.enrollment.email, b.enrollment.email);
          break;
        case 'submissionMode':
          result = compareText(modeLabel[a.enrollment.submissionMode] || a.enrollment.submissionMode, modeLabel[b.enrollment.submissionMode] || b.enrollment.submissionMode);
          break;
        case 'pledgeAmount':
          result = compareNumbers(a.enrollment.pledgeAmount, b.enrollment.pledgeAmount);
          break;
        case 'pledgeTier':
          result = compareText(a.enrollment.pledgeTier, b.enrollment.pledgeTier);
          break;
        case 'status':
          result = compareText(selectedMonthPaymentLabel(a), selectedMonthPaymentLabel(b));
          break;
        case 'paymentMethod':
          result = compareText(a.history.kind === 'due' ? '—' : (a.history.donation?.paymentMethod || a.history.paymentMethod || '—'), b.history.kind === 'due' ? '—' : (b.history.donation?.paymentMethod || b.history.paymentMethod || '—'));
          break;
        case 'source':
          result = compareText(a.source, b.source);
          break;
        case 'donationId':
          result = compareNumbers(a.history.kind === 'due' ? 0 : (a.history.donation?.id || 0), b.history.kind === 'due' ? 0 : (b.history.donation?.id || 0));
          break;
        case 'recordedOn':
          result = compareDates(a.history.kind === 'due' ? '' : (a.history.displayDate || a.history.donation?.date || ''), b.history.kind === 'due' ? '' : (b.history.displayDate || b.history.donation?.date || ''));
          break;
        case 'transactionId':
          result = compareText(a.history.kind === 'due' ? '—' : (a.history.donation?.txnRefId || a.history.subscription?.razorpayId || '—'), b.history.kind === 'due' ? '—' : (b.history.donation?.txnRefId || b.history.subscription?.razorpayId || '—'));
          break;
        case 'note':
          result = compareText(a.history.kind === 'due' ? normalizeDueNote(a.history.dueReason) : (a.history.donation?.note || '—'), b.history.kind === 'due' ? normalizeDueNote(b.history.dueReason) : (b.history.donation?.note || '—'));
          break;
        default:
          result = 0;
      }
      return applySortDirection(result, detailSort.direction);
    });

    return filtered;
  }, [detailData?.items, detailSort, enrollmentSourceFilter, paymentFilter]);

  const exportColumns: ExportColumn[] = React.useMemo(() => [
    { key: 'name', label: 'Name' },
    { key: 'whatsApp', label: 'WhatsApp' },
    { key: 'phone', label: 'Phone' },
    { key: 'email', label: 'Email' },
    { key: 'mode', label: 'Submission Mode' },
    { key: 'pledgeAmount', label: 'Pledge Amount (₹)' },
    { key: 'pledgeTier', label: 'Pledge Tier' },
    { key: 'thisMonth', label: 'This Month Status' },
    { key: 'method', label: 'Payment Method' },
    { key: 'source', label: 'Source' },
    { key: 'donationId', label: 'Donation ID' },
    { key: 'recordedOn', label: 'Recorded On' },
    { key: 'transactionId', label: 'Transaction ID' },
    { key: 'note', label: 'Note' },
  ], []);

  const exportRows = React.useMemo(() => filteredSortedItems.map((item) => {
    const rowStatus = selectedMonthPaymentLabel(item);
    const recordedOn = item.history.kind === 'due'
      ? ''
      : (item.history.displayDate
        ? dayjs(item.history.displayDate).format('D MMM YYYY')
        : (item.history.donation?.date ? dayjs(item.history.donation.date).format('D MMM YYYY') : ''));
    const note = item.history.kind === 'due' ? normalizeDueNote(item.history.dueReason) : (item.history.donation?.note || '');
    return {
      name: item.enrollment.fullName || '',
      whatsApp: item.enrollment.whatsAppNumber || '',
      phone: item.enrollment.phone || '',
      email: item.enrollment.email || '',
      mode: modeLabel[item.enrollment.submissionMode] || item.enrollment.submissionMode,
      pledgeAmount: Number(item.enrollment.pledgeAmount || 0).toFixed(0),
      pledgeTier: item.enrollment.pledgeTier || '',
      thisMonth: rowStatus,
      method: item.history.kind === 'due' ? '' : (item.history.donation?.paymentMethod || item.history.paymentMethod || ''),
      source: item.source || '',
      donationId: item.history.kind === 'due' ? '' : String(item.history.donation?.id || ''),
      recordedOn,
      transactionId: item.history.kind === 'due' ? '' : (item.history.donation?.txnRefId || item.history.subscription?.razorpayId || ''),
      note,
    };
  }), [filteredSortedItems]);

  const downloadReceipt = async (donationId: number) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/donation/receipt/${donationId}`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Failed to download receipt');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `donation-receipt-${donationId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading receipt:', err);
    }
  };

  const closeManualDonationModal = React.useCallback(() => {
    setManualTargetRow(null);
    setManualMode('create');
    setEditingDonationSnapshot(null);
  }, []);

  const openRecordDonationModal = (row: NityaSevaMonthDetailItem) => {
    const defaultFrequency = row.enrollment.paymentFrequency || 'monthly';
    setManualMode('create');
    setEditingDonationSnapshot(null);
    setManualPaymentFrequency(defaultFrequency);
    setManualAmount(String(getCycleAmount(Number(row.enrollment.pledgeAmount || 0), defaultFrequency)));
    setManualDate(dayjs().format('YYYY-MM-DD'));
    setManualForMonth(month || dayjs().format('YYYY-MM'));
    setManualTransactionId('');
    setManualNote('');
    setManualMethod(row.enrollment.submissionMode === 'cash_first_sunday' ? 'Cash' : 'Online');
    setManualTargetRow(row);
  };

  const openEditDonationModal = async (row: NityaSevaMonthDetailItem) => {
    if (!row.history.donation?.id) {
      return;
    }
    setSavingManualDonation(true);
    setError('');
    try {
      const donation = await fetchDonationForEdit(row.history.donation.id);
      const coverage = parseNityaSevaCoverageMeta(String(donation?.subPurpose || ''));
      const fallbackFrequency = row.enrollment.paymentFrequency || 'monthly';
      setManualMode('edit');
      setEditingDonationSnapshot(donation);
      setManualPaymentFrequency(coverage?.frequency || fallbackFrequency);
      setManualAmount(String(Number(donation?.amount || row.history.donation?.amount || 0)));
      setManualDate(donation?.date ? dayjs(String(donation.date)).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'));
      setManualForMonth(coverage?.forMonth || (month || dayjs().format('YYYY-MM')));
      setManualTransactionId(String(donation?.txnRefId || ''));
      setManualNote(String(donation?.note || ''));
      setManualMethod(String(donation?.paymentMethod || (row.enrollment.submissionMode === 'cash_first_sunday' ? 'Cash' : 'Online')));
      setManualTargetRow(row);
    } catch (_err) {
      setError('Failed to load donation for editing. Please try again.');
    } finally {
      setSavingManualDonation(false);
    }
  };

  const loadDetail = React.useCallback(async () => {
    if (!month) {
      setError('Missing month.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await fetchNityaSevaMonthDetail(month, { status, mode, q, limit: 500 });
      setDetailData(data);
    } catch (e) {
      setError('Failed to load Nitya Seva month details.');
    } finally {
      setLoading(false);
    }
  }, [month, mode, q, status]);

  const refreshDetailAfterMutation = React.useCallback(async () => {
    await loadDetail();
    const followUps = [700, 1500, 2800];
    followUps.forEach((delayMs) => {
      window.setTimeout(() => {
        void loadDetail();
      }, delayMs);
    });
  }, [loadDetail]);

  React.useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh' }}>
      <Header />
      <Sidebar />
      <Box
        component="main"
        className="MainContent"
        sx={{
          px: { xs: 2, md: 6 },
          pt: { xs: 'calc(12px + var(--Header-height))', sm: 'calc(12px + var(--Header-height))', md: 3 },
          pb: { xs: 2, sm: 2, md: 3 },
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          gap: 1,
          bgcolor: 'background.body',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PageBreadcrumbs
            links={[{ label: 'Nitya Seva', href: '/nitya-seva' }]}
            current={month ? `${dayjs(`${month}-01`).format('MMM YYYY')} Details` : 'Month Details'}
          />
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ flexWrap: 'wrap' }}>
          <Stack spacing={0.5}>
            <Typography level="h2" component="h1">Nitya Seva Month Details</Typography>
            <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
              {month ? `Month: ${dayjs(`${month}-01`).format('MMMM YYYY')}` : 'Month-specific Nitya Seva status'}
            </Typography>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <Button sx={{ width: { xs: '100%', sm: 'auto' } }} variant="soft" color="neutral" startDecorator={<FileDownloadRounded fontSize="small" />} onClick={() => setExportOpen(true)} disabled={!filteredSortedItems.length}>
              Export
            </Button>
            <Button sx={{ width: { xs: '100%', sm: 'auto' } }} variant="soft" color="neutral" onClick={() => navigate('/nitya-seva')}>
              Back to Nitya Seva
            </Button>
          </Stack>
        </Stack>

        <Card variant="outlined">
          <CardContent>
            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              spacing={1.25}
              alignItems={{ xs: 'stretch', lg: 'end' }}
              sx={{ flexWrap: 'wrap' }}
            >
              <FormControl sx={{ flex: { lg: '1 1 260px' }, width: '100%', minWidth: { xs: '100%', sm: 240 } }}>
                <FormLabel>Search</FormLabel>
                <Input
                  placeholder="Search name, email, or phone"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  startDecorator={<SearchRounded fontSize="small" />}
                />
              </FormControl>
              <FormControl sx={{ flex: { lg: '1 1 180px' }, width: '100%', minWidth: { xs: '100%', sm: 180 } }}>
                <FormLabel>Status</FormLabel>
                <Select value={status} onChange={(_, val) => setStatus((val as string) || '')} placeholder="Status">
                  <Option value="">All Status</Option>
                  <Option value="pending">Pending</Option>
                  <Option value="active">Active</Option>
                  <Option value="paused">Paused</Option>
                  <Option value="needs_follow_up">Needs Follow Up</Option>
                </Select>
              </FormControl>
              <FormControl sx={{ flex: { lg: '1 1 200px' }, width: '100%', minWidth: { xs: '100%', sm: 190 } }}>
                <FormLabel>Submission Mode</FormLabel>
                <Select value={mode} onChange={(_, val) => setMode((val as string) || '')} placeholder="Mode">
                  <Option value="">All Modes</Option>
                  <Option value="ecs_nach">ECS / NACH</Option>
                  <Option value="auto_razorpay">Auto Razorpay</Option>
                  <Option value="online_transfer">Online Transfer</Option>
                  <Option value="cash_first_sunday">Cash 1st Sunday</Option>
                </Select>
              </FormControl>
              <FormControl sx={{ flex: { lg: '1 1 170px' }, width: '100%', minWidth: { xs: '100%', sm: 170 } }}>
                <FormLabel>Enrollment</FormLabel>
                <Select value={enrollmentSourceFilter} onChange={(_, val) => setEnrollmentSourceFilter((val as string) || '')} placeholder="Enrollment source">
                  <Option value="">All Enrollments</Option>
                  <Option value="online">Online</Option>
                  <Option value="offline">Offline</Option>
                </Select>
              </FormControl>
              <FormControl sx={{ flex: { lg: '1 1 160px' }, width: '100%', minWidth: { xs: '100%', sm: 160 } }}>
                <FormLabel>Donation</FormLabel>
                <Select value={paymentFilter} onChange={(_, val) => setPaymentFilter((val as string) || '')} placeholder="Payment status">
                  <Option value="">All</Option>
                  <Option value="paid">Paid</Option>
                  <Option value="unpaid">Unpaid</Option>
                </Select>
              </FormControl>
              <Button onClick={loadDetail} loading={loading} startDecorator={!loading ? <RefreshRounded fontSize="small" /> : undefined} sx={{ minWidth: 132, width: { xs: '100%', lg: 'auto' }, flexShrink: 0 }}>
                Refresh
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {loading ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
            <CircularProgress size="sm" />
            <Typography level="body-sm">Loading month details...</Typography>
          </Stack>
        ) : null}

        {error ? <Alert color="danger">{error}</Alert> : null}

        {detailData ? (
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ flexWrap: 'wrap' }}>
              <Chip variant="soft" color="primary">{detailData.summary.totalEnrollments} enrollments</Chip>
              <Chip variant="soft" color="success">{detailData.summary.paidCount} paid for this month</Chip>
              <Chip variant="soft" color={detailData.summary.dueCount > 0 ? 'danger' : 'neutral'}>{detailData.summary.dueCount} not paid yet</Chip>
              <Chip variant="soft" color="warning">Expected ₹{detailData.summary.totalExpected.toFixed(0)}</Chip>
              <Chip variant="soft" color="success">Collected ₹{detailData.summary.totalCollected.toFixed(0)}</Chip>
              <Chip variant="soft" color={detailData.summary.pendingAmount > 0 ? 'danger' : 'neutral'}>Pending ₹{detailData.summary.pendingAmount.toFixed(0)}</Chip>
            </Stack>

            <Card variant="outlined">
              <CardContent>
                <Typography level="title-md" sx={{ mb: 1 }}>Enrollment Table</Typography>
                <Typography level="body-sm" sx={{ color: 'text.tertiary', mb: 1.5 }}>
                  See clearly who has paid for this month, who has not paid yet, and take action directly from this table.
                </Typography>
                <Sheet variant="outlined" sx={{ borderRadius: 'lg', overflow: 'hidden', borderColor: 'divider', backgroundColor: 'background.surface' }}>
                  <Box sx={{ width: '100%', overflowX: 'auto' }}>
                    <Table
                      stickyHeader
                      hoverRow
                      sx={{
                        '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
                        '--Table-headerUnderlineThickness': '1px',
                        '--TableCell-paddingY': '10px',
                        '--TableCell-paddingX': '14px',
                        tableLayout: 'fixed',
                        minWidth: 1540,
                        '& thead th': {
                          textTransform: 'uppercase',
                          fontSize: '0.7rem',
                          letterSpacing: 0.6,
                          color: 'text.secondary',
                          whiteSpace: 'nowrap',
                        },
                        '& tbody td': {
                          verticalAlign: 'middle',
                        },
                        '& thead th:last-child': {
                          position: 'sticky',
                          right: 0,
                          top: 0,
                          zIndex: 30,
                          backgroundColor: 'var(--joy-palette-background-level1)',
                          boxShadow: '-1px 0 0 var(--joy-palette-divider)',
                          textAlign: 'center',
                          width: '96px',
                          minWidth: '96px',
                          maxWidth: '96px',
                          paddingLeft: '10px',
                          paddingRight: '10px',
                        },
                        '& tbody tr:nth-of-type(even)': {
                          backgroundColor: 'var(--joy-palette-background-level1)',
                        },
                        '& tbody tr:hover': {
                          backgroundColor: 'var(--joy-palette-background-level2)',
                        },
                        '& tbody td:last-child': {
                          position: 'sticky',
                          right: 0,
                          zIndex: 20,
                          backgroundColor: 'background.surface',
                          boxShadow: '-1px 0 0 var(--joy-palette-divider)',
                          textAlign: 'center',
                          width: '96px',
                          minWidth: '96px',
                          maxWidth: '96px',
                          paddingLeft: '10px',
                          paddingRight: '10px',
                        },
                        '& tbody tr:nth-of-type(even) td:last-child': {
                          backgroundColor: 'var(--joy-palette-background-level1)',
                        },
                        '& tbody tr:hover td:last-child': {
                          backgroundColor: 'var(--joy-palette-background-level2)',
                        },
                      }}
                    >
                      <colgroup>
                        <col style={{ width: '210px' }} />
                        <col style={{ width: '170px' }} />
                        <col style={{ width: '170px' }} />
                        <col style={{ width: '220px' }} />
                        <col style={{ width: '150px' }} />
                        <col style={{ width: '100px' }} />
                        <col style={{ width: '100px' }} />
                        <col style={{ width: '120px' }} />
                        <col style={{ width: '110px' }} />
                        <col style={{ width: '120px' }} />
                        <col style={{ width: '120px' }} />
                        <col style={{ width: '180px' }} />
                        <col style={{ width: '220px' }} />
                        <col style={{ width: '240px' }} />
                        <col style={{ width: '96px' }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th>{renderSortableHeader('Name', detailSort.key === 'fullName', detailSort.direction, () => handleDetailSort('fullName'))}</th>
                          <th>{renderSortableHeader('WhatsApp', detailSort.key === 'whatsAppNumber', detailSort.direction, () => handleDetailSort('whatsAppNumber'))}</th>
                          <th>{renderSortableHeader('Phone', detailSort.key === 'phone', detailSort.direction, () => handleDetailSort('phone'))}</th>
                          <th>{renderSortableHeader('Email', detailSort.key === 'email', detailSort.direction, () => handleDetailSort('email'))}</th>
                          <th>{renderSortableHeader('Mode', detailSort.key === 'submissionMode', detailSort.direction, () => handleDetailSort('submissionMode'))}</th>
                          <th>{renderSortableHeader('Amount', detailSort.key === 'pledgeAmount', detailSort.direction, () => handleDetailSort('pledgeAmount'))}</th>
                          <th>{renderSortableHeader('Tier', detailSort.key === 'pledgeTier', detailSort.direction, () => handleDetailSort('pledgeTier'))}</th>
                          <th>{renderSortableHeader('This Month', detailSort.key === 'status', detailSort.direction, () => handleDetailSort('status'))}</th>
                          <th>{renderSortableHeader('Method', detailSort.key === 'paymentMethod', detailSort.direction, () => handleDetailSort('paymentMethod'))}</th>
                          <th>{renderSortableHeader('Source', detailSort.key === 'source', detailSort.direction, () => handleDetailSort('source'))}</th>
                          <th>{renderSortableHeader('Donation ID', detailSort.key === 'donationId', detailSort.direction, () => handleDetailSort('donationId'))}</th>
                          <th>{renderSortableHeader('Recorded On', detailSort.key === 'recordedOn', detailSort.direction, () => handleDetailSort('recordedOn'))}</th>
                          <th>{renderSortableHeader('Transaction ID', detailSort.key === 'transactionId', detailSort.direction, () => handleDetailSort('transactionId'))}</th>
                          <th>{renderSortableHeader('Note', detailSort.key === 'note', detailSort.direction, () => handleDetailSort('note'))}</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSortedItems.length ? filteredSortedItems.map((item) => {
                          const rowStatus = selectedMonthPaymentLabel(item);
                          return (
                            <tr key={`${item.enrollment.id}-${item.history.month || item.history.donation?.id || item.enrollment.userId}`}>
                              <td>{item.enrollment.fullName}</td>
                              <td>{item.enrollment.whatsAppNumber || '—'}</td>
                              <td>{item.enrollment.phone || '—'}</td>
                              <td>
                                <Typography level="body-sm" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={item.enrollment.email || '—'}>
                                  {item.enrollment.email || '—'}
                                </Typography>
                              </td>
                              <td>{modeLabel[item.enrollment.submissionMode] || item.enrollment.submissionMode}</td>
                              <td>₹{item.enrollment.pledgeAmount.toFixed(0)}</td>
                              <td>{item.enrollment.pledgeTier}</td>
                              <td>
                                <Chip size="sm" variant="soft" color={statusColor(rowStatus)}>{rowStatus}</Chip>
                              </td>
                              <td>{item.history.kind === 'due' ? '—' : (item.history.donation?.paymentMethod || item.history.paymentMethod || '—')}</td>
                              <td>{item.source}</td>
                              <td>
                                <Typography level="body-sm" sx={{ fontFamily: 'monospace' }}>
                                  {item.history.kind === 'due' ? '—' : (item.history.donation?.id || '—')}
                                </Typography>
                              </td>
                              <td>{item.history.kind === 'due' ? '—' : (item.history.displayDate ? dayjs(item.history.displayDate).format('D MMM YYYY') : (item.history.donation?.date ? dayjs(item.history.donation.date).format('D MMM YYYY') : '—'))}</td>
                              <td>
                                <Typography
                                  level="body-sm"
                                  sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}
                                  title={item.history.kind === 'due' ? '—' : (item.history.donation?.txnRefId || item.history.subscription?.razorpayId || '—')}
                                >
                                  {item.history.kind === 'due' ? '—' : (item.history.donation?.txnRefId || item.history.subscription?.razorpayId || '—')}
                                </Typography>
                              </td>
                              <td>
                                <Typography
                                  level="body-sm"
                                  sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                  title={item.history.kind === 'due' ? normalizeDueNote(item.history.dueReason) : (item.history.donation?.note || '—')}
                                >
                                  {item.history.kind === 'due' ? normalizeDueNote(item.history.dueReason) : (item.history.donation?.note || '—')}
                                </Typography>
                              </td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                <Stack direction="row" spacing={1} justifyContent="center">
                                  <Tooltip title="View enrollment">
                                    <IconButton size="sm" variant="soft" color="neutral" onClick={() => navigate(`/nitya-seva/details?id=${item.enrollment.id}`)}>
                                      <VisibilityRounded fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  {item.history.kind === 'due' ? (
                                    <Tooltip title="Record donation">
                                      <IconButton size="sm" variant="soft" color="primary" onClick={() => openRecordDonationModal(item)}>
                                        <FavoriteRounded fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  ) : item.history.donation?.id ? (
                                    <>
                                      <Tooltip title="Edit donation">
                                        <IconButton size="sm" variant="soft" color="neutral" onClick={() => { void openEditDonationModal(item); }}>
                                          <EditRounded fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Download receipt">
                                        <IconButton size="sm" variant="soft" onClick={() => downloadReceipt(Number(item.history.donation?.id))}>
                                          <ReceiptLong fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </>
                                  ) : null}
                                </Stack>
                              </td>
                            </tr>
                          );
                        }) : (
                          <tr>
                            <td colSpan={15}><Typography level="body-sm">No month-specific enrollment rows found.</Typography></td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </Box>
                </Sheet>
              </CardContent>
            </Card>
          </Stack>
        ) : null}

        <Modal open={Boolean(manualTargetRow)} onClose={() => { if (!savingManualDonation) closeManualDonationModal(); }}>
          <ModalDialog sx={{ width: 'min(94vw, 560px)' }}>
            <ModalClose disabled={savingManualDonation} />
            <Typography level="title-lg">{manualMode === 'edit' ? 'Edit Donation' : 'Record Manual Donation'}</Typography>
            <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
              {manualTargetRow
                ? `${manualMode === 'edit' ? 'Update' : 'For'} ${manualTargetRow.enrollment.fullName} • ${month ? dayjs(`${month}-01`).format('MMM YYYY') : ''} • ${getPaymentFrequencyLabel(manualPaymentFrequency)} cycle`
                : (manualMode === 'edit' ? 'Update recorded donation' : 'Record donation for selected month')}
            </Typography>
            {savingManualDonation ? (
              <Alert color="primary" variant="soft">
                {manualMode === 'edit'
                  ? 'Updating donation and refreshing this month view. Please wait...'
                  : 'Recording donation and updating this month view. Please wait...'}
              </Alert>
            ) : null}
            {manualTargetRow ? (
              <Alert color="primary" variant="soft">
                Monthly pledge: ₹{Number(manualTargetRow.enrollment.pledgeAmount || 0).toLocaleString('en-IN')} • Suggested cycle amount: ₹{getCycleAmount(Number(manualTargetRow.enrollment.pledgeAmount || 0), manualPaymentFrequency).toLocaleString('en-IN')}
              </Alert>
            ) : null}
            <FormControl>
              <FormLabel>Payment Frequency</FormLabel>
              <RadioGroup
                value={manualPaymentFrequency}
                onChange={(e) => {
                  const nextFrequency = e.target.value as NityaSevaPaymentFrequency;
                  setManualPaymentFrequency(nextFrequency);
                  if (manualTargetRow) {
                    setManualAmount(String(getCycleAmount(Number(manualTargetRow.enrollment.pledgeAmount || 0), nextFrequency)));
                  }
                }}
              >
                {paymentFrequencyOptions.map((option) => (
                  <Radio
                    key={option.value}
                    value={option.value}
                    disabled={savingManualDonation}
                    label={`${option.label} (${option.months === 1 ? 'pay every month' : `pay once for ${option.months} months`})`}
                  />
                ))}
              </RadioGroup>
            </FormControl>
            <Input disabled={savingManualDonation} type="number" placeholder="Amount" value={manualAmount} onChange={(e) => setManualAmount(e.target.value)} />
            <Input disabled={savingManualDonation} type="month" value={manualForMonth} onChange={(e) => setManualForMonth(e.target.value)} />
            <Input disabled={savingManualDonation} type="date" value={manualDate} onChange={(e) => setManualDate(e.target.value)} />
            <Input disabled={savingManualDonation} placeholder="Transaction ID (optional)" value={manualTransactionId} onChange={(e) => setManualTransactionId(e.target.value)} />
            <Select disabled={savingManualDonation} value={manualMethod} onChange={(_, val) => setManualMethod((val as string) || 'Cash')}>
              <Option value="Cash">Cash</Option>
              <Option value="Online">Online</Option>
              <Option value="Bank Transfer">Bank Transfer</Option>
            </Select>
            <Textarea disabled={savingManualDonation} minRows={3} placeholder="Note (optional)" value={manualNote} onChange={(e) => setManualNote(e.target.value)} />
            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Button variant="plain" disabled={savingManualDonation} onClick={closeManualDonationModal}>
                Cancel
              </Button>
              <Button
                loading={savingManualDonation}
                onClick={async () => {
                  if (!manualTargetRow) return;
                  setSavingManualDonation(true);
                  setError('');
                  try {
                    if (manualMode === 'edit' && editingDonationSnapshot?.id) {
                      await updateDonationById(Number(editingDonationSnapshot.id), {
                        ...editingDonationSnapshot,
                        amount: Number(manualAmount),
                        date: dayjs(manualDate).format('YYYY-MM-DDT12:00:00Z'),
                        paymentMethod: manualMethod,
                        txnRefId: manualTransactionId,
                        note: manualNote,
                        subPurpose: buildNityaSevaCoverageMeta(manualForMonth, manualPaymentFrequency),
                      });
                    } else {
                      await recordNityaSevaManualDonation(manualTargetRow.enrollment.id, {
                        amount: Number(manualAmount),
                        date: manualDate,
                        forMonth: manualForMonth,
                        paymentFrequency: manualPaymentFrequency,
                        paymentMethod: manualMethod,
                        transactionId: manualTransactionId,
                        note: manualNote,
                      });
                    }
                    closeManualDonationModal();
                    await refreshDetailAfterMutation();
                  } catch (_saveErr) {
                    setError(manualMode === 'edit' ? 'Failed to update donation. Please try again.' : 'Failed to record manual donation. Please try again.');
                  } finally {
                    setSavingManualDonation(false);
                  }
                }}
              >
                {manualMode === 'edit' ? 'Update Donation' : 'Save Donation'}
              </Button>
            </Stack>
          </ModalDialog>
        </Modal>

        <ExportModal
          open={exportOpen}
          onClose={() => setExportOpen(false)}
          columns={exportColumns}
          rows={exportRows}
          filename={`nitya-seva-${month || 'month'}-details`}
          title={month ? `Nitya Seva – ${dayjs(`${month}-01`).format('MMMM YYYY')}` : 'Nitya Seva Month Details'}
        />
      </Box>
    </Box>
  );
}